import { z } from "zod/v4";

import {
  E_UsagePeriod,
  USAGE_FEATURE_DISPLAY_NAMES,
  USAGE_FEATURE_UNIT_NAMES,
  z_UsageLimitExceededCause,
  type T_UsageFeature,
  type T_UsageFeatureStats,
  type T_UsageLimitExceededCause,
} from "@lattice/core-lib/types/usage";

const z_UsageLimitErrorEnvelope = z.object({
  data: z.object({
    messageLimitError: z.unknown(),
  }),
});

const RESET_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

export const USAGE_WARNING_THRESHOLD_PERCENT = 75;

export type T_UsageFeatureProjection = {
  allowanceLabel: string;
  bonusLabel: string | null;
  displayName: string;
  feature: T_UsageFeature;
  limit: number | null;
  percentUsed: number;
  remaining: number | null;
  remainingLabel: string;
  resetAt: Date;
  resetLabel: string;
  used: number;
  usedLabel: string;
};

function pluralizeUnit(unit: string, amount: number): string {
  return amount === 1 ? unit : `${unit}s`;
}

function getPeriodNoun(period: T_UsageFeatureStats["period"]): string {
  return period === E_UsagePeriod.MONTHLY ? "month" : "day";
}

export function formatUsageResetLabel(resetAt: Date | string): string {
  const date = resetAt instanceof Date ? resetAt : new Date(resetAt);
  if (!Number.isFinite(date.getTime())) return "Reset date unavailable";
  return `Resets ${RESET_DATE_FORMATTER.format(date)}`;
}

export function projectUsageFeature(
  feature: T_UsageFeatureStats,
): T_UsageFeatureProjection {
  const used = Math.max(0, feature.used);
  const limit = feature.effective_limit;
  const unit = USAGE_FEATURE_UNIT_NAMES[feature.feature];
  const resetAt = new Date(feature.next_cycle);

  if (limit === null) {
    return {
      allowanceLabel: "Included with your plan",
      bonusLabel: null,
      displayName: USAGE_FEATURE_DISPLAY_NAMES[feature.feature],
      feature: feature.feature,
      limit,
      percentUsed: 0,
      remaining: null,
      remainingLabel: "Plan managed",
      resetAt,
      resetLabel: formatUsageResetLabel(resetAt),
      used,
      usedLabel: "Plan managed",
    };
  }

  const normalizedLimit = Math.max(0, limit);
  const bonus = Math.max(0, feature.bonus_credits);
  const remaining = Math.max(normalizedLimit - used, 0);
  const percentUsed =
    normalizedLimit === 0 ? 100 : Math.min((used / normalizedLimit) * 100, 100);
  const periodNoun = getPeriodNoun(feature.period);

  return {
    allowanceLabel: `${normalizedLimit} ${pluralizeUnit(unit, normalizedLimit)} available this ${periodNoun}`,
    bonusLabel:
      bonus > 0
        ? `${bonus} bonus ${pluralizeUnit(unit, bonus)} included`
        : null,
    displayName: USAGE_FEATURE_DISPLAY_NAMES[feature.feature],
    feature: feature.feature,
    limit: normalizedLimit,
    percentUsed,
    remaining,
    remainingLabel: `${remaining} ${pluralizeUnit(unit, remaining)} remaining`,
    resetAt,
    resetLabel: formatUsageResetLabel(resetAt),
    used,
    usedLabel: `${used} / ${normalizedLimit}`,
  };
}

export function getUsageFeatureProjection(
  features: readonly T_UsageFeatureStats[],
  feature: T_UsageFeature,
): T_UsageFeatureProjection | null {
  const stats = features.find((candidate) => candidate.feature === feature);
  return stats ? projectUsageFeature(stats) : null;
}

export function getUsageWarningPercent(
  projection: T_UsageFeatureProjection | null,
): number | null {
  if (
    projection === null ||
    projection.limit === null ||
    projection.percentUsed < USAGE_WARNING_THRESHOLD_PERCENT
  ) {
    return null;
  }
  return Math.round(projection.percentUsed);
}

export function parseUsageLimitError(
  error: unknown,
): T_UsageLimitExceededCause | null {
  const direct = z_UsageLimitExceededCause.safeParse(error);
  if (direct.success) return direct.data;

  const envelope = z_UsageLimitErrorEnvelope.safeParse(error);
  if (!envelope.success) return null;
  const parsed = z_UsageLimitExceededCause.safeParse(
    envelope.data.data.messageLimitError,
  );
  return parsed.success ? parsed.data : null;
}

export function formatUsageLimitErrorMessage(
  details: T_UsageLimitExceededCause,
): string {
  const period = details.period === E_UsagePeriod.MONTHLY ? "monthly" : "daily";
  return `You've reached your ${period} Prism limit. ${formatUsageResetLabel(details.resetAt)}.`;
}

export function getUsageLimitErrorMessage(error: unknown): string | null {
  const details = parseUsageLimitError(error);
  return details ? formatUsageLimitErrorMessage(details) : null;
}
